import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useTutorialStore, selectAnyTourActive, selectInTutorialProject, selectInExampleProject } from '@/store/tutorialStore.ts';
import { capabilities } from '@/capabilities.ts';
import { getAudioContext, playSilentKick } from '@/audio/audioProcessor.ts';
import { ExportHubDialog } from '@/export/ExportHubDialog.tsx';
import { AiTtsDialog } from './AiTtsDialog.tsx';
import { hasSeenOrient, hasSeenOrientReview, orientAutoArmMode } from '@/hooks/useQuickOrient.ts';
import { GenerationHistoryPanel } from './GenerationHistoryPanel.tsx';
import { ShareDialog } from './ShareDialog.tsx';
import { ConnectionStatus } from './ConnectionStatus.tsx';
import { AccountButton } from './AccountButton.tsx';
import { AccountDialogLayer } from './AccountDialogLayer.tsx';
import { EarnCreditsDialog } from './EarnCreditsDialog.tsx';
import { RewardNotifier } from './RewardNotifier.tsx';
import styles from './Transport.module.css';

// ── SVG Icons ────────────────────────────────────────────────────────

const IconSkipBack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <rect x="4" y="5" width="2.5" height="14" rx="0.5" />
    <path d="M19 6.27v11.46a1 1 0 01-1.52.86L8.12 12.86a1 1 0 010-1.72l9.36-5.73A1 1 0 0119 6.27z" />
  </svg>
);

const IconPlay = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z" />
  </svg>
);

const IconPause = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="4" width="5" height="16" rx="1" />
    <rect x="14" y="4" width="5" height="16" rx="1" />
  </svg>
);

const IconSkipForward = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <rect x="17.5" y="5" width="2.5" height="14" rx="0.5" />
    <path d="M5 6.27v11.46a1 1 0 001.52.86l9.36-5.73a1 1 0 000-1.72L6.52 5.41A1 1 0 005 6.27z" />
  </svg>
);

function formatTimecode(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toFixed(1).padStart(4, '0')}`;
}

export function Transport() {
  const { t } = useTranslation('transport');
  const currentTime = usePlaybackStore(s => s.currentTime);
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const togglePlayback = usePlaybackStore(s => s.togglePlayback);
  const seek = usePlaybackStore(s => s.seek);
  const pixelsPerSecond = useUiStore(s => s.pixelsPerSecond);
  const setZoom = useUiStore(s => s.setZoom);
  const project = useProjectStore(s => s.project);

  const getViewOffset = useProjectStore(s => s.getViewOffset);
  const getViewDuration = useProjectStore(s => s.getViewDuration);
  useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  const shuttleSpeed = usePlaybackStore(s => s.shuttleSpeed);
  const reviewAutoPlay = useUiStore(s => s.reviewAutoPlay);
  const setReviewAutoPlay = useUiStore(s => s.setReviewAutoPlay);
  const allShots = useProjectStore(s => s.allShots);
  const readOnly = useProjectStore(s => s.readOnly);
  const sessionId = useProjectStore(s => s.sessionId);
  const reviewSessionId = useProjectStore(s => s.reviewSessionId);
  const earnCreditsDialogOpen = useAccountStore(s => s.earnCreditsDialogOpen);
  const setEarnCreditsDialogOpen = useAccountStore(s => s.setEarnCreditsDialogOpen);
  const historyPanelOpen = useUiStore(s => s.historyPanelOpen);
  const openHelpDialog = useUiStore(s => s.openHelpDialog);
  const isAnyGenerating = useAiStore(s => s.isAnyGenerating);
  const aiWorkspaceOpen = useUiStore(s => s.aiWorkspaceOpen);


  const viewOffset = getViewOffset();
  const viewDuration = getViewDuration();
  const displayTime = currentTime - viewOffset;

  const exportHubOpen = useUiStore(s => s.exportHubOpen);
  const setExportHubOpen = useUiStore(s => s.setExportHubOpen);
  const exportHubInitialTab = useUiStore(s => s.exportHubInitialTab);
  const aiTtsOpen = useUiStore(s => s.aiTtsDialogOpen);
  const closeAiTtsDialog = useUiStore(s => s.closeAiTtsDialog);
  const shareOpen = useUiStore(s => s.shareDialogOpen);
  const setShareOpen = useUiStore(s => s.setShareDialogOpen);
  const [subscriptionGateOpen, setSubscriptionGateOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const isSubscribed = useAccountStore(s => s.isSubscribed);

  const aboutRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const playbackRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);

  // The bar can wrap to two rows now (flex-wrap), so its height is no longer the static
  // --transport-height + --safe-bottom. Toast.module.css anchors above the bar via
  // --transport-rendered-h; publish the real rendered height and KEEP IT CURRENT on wrap
  // changes. Cleanup must remove the property or a stale value mis-positions toasts on the
  // Board/Script tabs after leaving the Timeline (offsetHeight already includes the
  // safe-bottom padding).
  //
  // Compact-row selection is fit-measured here, not a device-class media query (QA
  // 2026-08-13, issue 6): sum the THREE groups' natural (unstretched) children widths — they
  // keep natural width in both modes, so the measurement is independent of the currently
  // applied mode, which means no flip-flop/hysteresis is needed — and flip to the .compact
  // layout only when that sum doesn't fit the bar's available width.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const naturalW = (el: HTMLElement | null) => {
      if (!el) return 0;
      const kids = Array.from(el.children) as HTMLElement[];
      if (!kids.length) return 0;
      const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
      return kids.reduce((w, k) => w + k.getBoundingClientRect().width, 0) + gap * (kids.length - 1);
    };
    const apply = () => {
      document.documentElement.style.setProperty('--transport-rendered-h', `${bar.offsetHeight}px`);
      const cs = getComputedStyle(bar);
      const avail = bar.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      const colGap = parseFloat(cs.columnGap) || 8;
      const groups = [playbackRef.current, statusRef.current, actionsRef.current]
        .filter((g): g is HTMLDivElement => !!g);
      // +1 gap per group: the (hidden-in-compact) spacer occupies a gap slot in the
      // single-row layout.
      const needed = groups.reduce((w, g) => w + naturalW(g), 0) + colGap * groups.length;
      setCompact(needed > avail);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    // Leaf groups are never main-axis-stretched in either mode, so their sizes are
    // mode-independent → no measure/re-layout oscillation. Observing them catches
    // content-width changes (credits digits, locale, conditional buttons).
    [playbackRef.current, statusRef.current, actionsRef.current].forEach(g => g && ro.observe(g));
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--transport-rendered-h');
    };
  }, [readOnly]); // group set (actionsGroup mount) changes with mode

  // Close popovers on outside click
  useEffect(() => {
    if (!aboutOpen) return;
    const handler = (e: MouseEvent) => {
      if (aboutOpen && aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aboutOpen]);

  // Auto-show Quick Orient coach marks on first project open
  const setOrientStep = useUiStore(s => s.setOrientStep);
  // 20260921 — ANY TOUR (design §2.1): Quick Orient's auto-arm defers to a tour owning the screen,
  // whichever script it is. The characters tour reaches the Timeline too (its finale card follows
  // the user across Breakdown / Boards / Timeline), and coach marks over it would cover its exits.
  const tutorialActive = useTutorialStore(selectAnyTourActive);
  // 20260922 (owner) — AND NEVER INSIDE A TUTORIAL PROJECT AT ALL, tour running or not: "Can we
  // make it such-that the Breakdown tab and Timeline tab tutorials … don't show up from within any of
  // the tutorial projects? … Better to reserve it for when the user is back in their own project as
  // it serves as a reminder then." The tour flag above could not carry that on its own, because the
  // tour ENDS inside the tutorial copy (End tour, or the finale's Play your scene) and the marks
  // fired the moment it did — which is the collision the owner is describing. This one is the
  // DOCUMENT's identity (setSqlitePath's `tutorial_project`, either tour's copy), so it outlives the
  // tour and survives the WebView recreate that reloads the page without startTutorial. The seen
  // flag is untouched, so the user's own next project is where the marks land.
  const inTutorialProject = useTutorialStore(selectInTutorialProject);
  // 20261009 (owner) — and never inside the bundled EXAMPLE project either: it is a New-user-guide
  // project (minted fresh on every open), and "those tutorials are for orienting users on their
  // first custom project". Same deferral: the seen flag is untouched.
  const inExampleProject = useTutorialStore(selectInExampleProject);
  // NOT gated on a running GUIDE (T12), and that is a 20260903 reversal. It used to defer here
  // while a guide was active, which made orient fire the instant the guide ENDED — and the
  // example guide ends the moment playback starts, so the coach marks landed on top of the
  // playing scene. The priority now runs the other way: orient arms on Timeline entry exactly as
  // it does for any first visit, and GuideRail PARKS its card (and glow) while orient is showing
  // or about to show, resuming on the same step once the marks are dismissed. The owner's
  // wording: "enter the Timeline tab, have the Timeline tutorial play, then when it concludes
  // have the Examples tutorial resume instructing you to press the Play button."
  //
  // IS gated on the AI workspace (20260904, owner): the Board's "Open AI Studio" route hop mounts
  // this view with the workspace already open over it, so without the gate the marks fired on top
  // of a surface that hides every one of their targets. The rule (orientAutoArmMode, unit-tested
  // in useQuickOrient.test.ts) is that orient arms only when the Timeline is actually showing;
  // aiWorkspaceOpen is a dependency so a workspace close that falls back to the Timeline arms it
  // then — a returnToBoard close unmounts this view instead and the seen-flag stays untouched.
  useEffect(() => {
    const mode = orientAutoArmMode({
      hasProject: !!project,
      tutorialActive,
      inTutorialProject,
      inExampleProject,
      aiWorkspaceOpen,
      readOnly,
      seenOrient: hasSeenOrient(),
      seenOrientReview: hasSeenOrientReview(),
    });
    if (mode === null) return;
    const timer = setTimeout(() => setOrientStep(0), 600);
    return () => clearTimeout(timer);
  }, [project, readOnly, setOrientStep, tutorialActive, inTutorialProject, inExampleProject, aiWorkspaceOpen]);


  return (
    <div className={`${styles.transport} ${compact ? styles.compact : ''}`} ref={barRef}>
      {/* ── Playback group ────────────────────────────────── */}
      <div className={styles.playbackGroup} ref={playbackRef}>
        {/* Skip back one shot */}
        <button className={styles.btn} disabled={aiWorkspaceOpen || isAnyGenerating()} onClick={() => {
          if (aiWorkspaceOpen || isAnyGenerating()) return;
          const shots = allShots();
          let cumulative = 0;
          let prevStart = 0;
          for (const shot of shots) {
            if (cumulative >= currentTime - 0.05) break;
            prevStart = cumulative;
            cumulative += shot.duration;
          }
          seek(prevStart);
        }} title={t('previousShot')}>
          <IconSkipBack />
        </button>

        {/* Play / Pause */}
        {/* data-glow-target: guided-flow wiring (T12), unconditional — the example-project
            guide's second step points here. See GuideRail. */}
        <button className={`${styles.btn} ${styles.playBtn}`} data-glow-target="transport-play" disabled={aiWorkspaceOpen} onClick={() => {
          // Safari: resume + silent kick in the user gesture call stack.
          // Safari can report ctx.state=running but not output audio if the
          // context was created without a gesture. The silent kick forces
          // WebKit to connect its internal audio hardware pipe.
          if (!isPlaying) {
            try {
              const ctx = getAudioContext();
              ctx.resume().then(() => playSilentKick(ctx)).catch(() => {});
              playSilentKick(ctx); // also kick synchronously for immediate effect
            } catch { /* ok */ }
          }
          togglePlayback();
        }} title={t('playPause')}>
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>

        {/* Skip forward one shot */}
        <button className={styles.btn} disabled={aiWorkspaceOpen || isAnyGenerating()} onClick={() => {
          if (aiWorkspaceOpen || isAnyGenerating()) return;
          const shots = allShots();
          let cumulative = 0;
          for (const shot of shots) {
            cumulative += shot.duration;
            if (cumulative > currentTime + 0.05) {
              seek(cumulative);
              return;
            }
          }
        }} title={t('nextShot')}>
          <IconSkipForward />
        </button>

        {/* Timecode */}
        <span className={styles.timecode}>{formatTimecode(displayTime)}</span>
        <span className={styles.separator}>/</span>
        <span className={styles.totalTime}>{formatTimecode(viewDuration)}</span>

        {/* Shuttle speed indicator */}
        {shuttleSpeed !== 1 && isPlaying && (
          <span className={styles.shuttleIndicator}>
            {Math.abs(shuttleSpeed)}x {shuttleSpeed > 0 ? '\u25B6' : '\u25C0'}
          </span>
        )}

        {/* Step toggle (formerly Auto-Pause) */}
        <button
          className={`${styles.stepBtn} ${reviewAutoPlay ? styles.stepActive : ''}`}
          disabled={aiWorkspaceOpen}
          onClick={() => setReviewAutoPlay(!reviewAutoPlay)}
          title={t('stepButtonTooltip')}
        >
          {t('stepButtonLabel')}
        </button>

        {/* Zoom slider */}
        <div className={styles.zoomControls}>
          <input
            type="range"
            className={styles.zoomSlider}
            min={20}
            max={300}
            step={10}
            value={pixelsPerSecond}
            disabled={aiWorkspaceOpen}
            onChange={(e) => setZoom(Number(e.target.value))}
            title={t('zoomTooltip')}
          />
        </div>
      </div>

      <div className={styles.spacer} />

      {/* ── Status group ──────────────────────────────────── */}
      <div className={styles.statusGroup} ref={statusRef}>
        {/* Review mode badge */}
        {readOnly && <span className={styles.reviewBadge}>{t('reviewModeBadge')}</span>}

        {/* Connection status (review + embedded) */}
        {(sessionId || reviewSessionId) && (
          <ConnectionStatus sessionId={(sessionId ?? reviewSessionId)!} />
        )}


        {/* Help button — review mode */}
        {readOnly && (
          <button
            className={styles.helpBtn}
            onClick={openHelpDialog}
            title={t('helpTooltip')}
          >
            ?
          </button>
        )}

        {/* About — review mode only */}
        {readOnly && (
          <div className={styles.aboutWrapper} ref={aboutRef}>
            <button
              className={styles.aboutBtn}
              onClick={() => setAboutOpen(!aboutOpen)}
              title={t('aboutButtonLabel')}
            >
              {t('aboutButtonLabel')}
            </button>
            {aboutOpen && (
              <div className={styles.aboutPopover}>
                <p className={styles.aboutTitle}>{t('aboutTitle')}</p>
                <p className={styles.aboutDesc}>
                  {t('aboutDescription')}
                </p>
                <a
                  className={styles.aboutCta}
                  href="https://previspro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('aboutCtaLink')}
                </a>
                <p className={styles.aboutCopy}>{t('aboutCopyright')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Actions group ─────────────────────────────────── */}
      {!readOnly && (capabilities.fileIO || capabilities.aiGeneration || capabilities.sharing) && (
        <div className={styles.actionsGroup} ref={actionsRef}>
          {/* Gear icon (Export) hidden — playerTemplate sync paused, see CLAUDE.md */}

          <div className={styles.helpCreditsGroup}>
            {/* Help button */}
            <button
              className={styles.helpBtn}
              onClick={openHelpDialog}
              title={t('helpTooltip')}
            >
              ?
            </button>

            {/* Account button (credits) */}
            {capabilities.aiGeneration && <AccountButton />}
          </div>

          <div className={styles.exportShareGroup}>
            {/* Export button — visible in standalone and embedded; hidden in
                reviewer / online-review mode via the outer !readOnly gate. */}
            <button
              className={styles.actionBtn}
              onClick={() => useUiStore.getState().setExportHubOpen(true)}
              disabled={!project}
              title={t('exportButtonTooltip')}
            >
              {t('exportButtonLabel')}
            </button>

            {/* Share button */}
            {capabilities.sharing && (
              <button
                className={styles.shareBtn}
                onClick={() => {
                  if (capabilities.bridge && !isSubscribed) {
                    setSubscriptionGateOpen(true);
                  } else {
                    setShareOpen(true);
                  }
                }}
                disabled={!project}
                title={t('shareButtonTooltip')}
              >
                {t('shareButtonLabel')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Dialogs ───────────────────────────────────────── */}
      {/* The AI-error / sign-in / paywall block moved out on 2026-08-21 so the Board route can
          host it too (see AccountDialogLayer). Mounted HERE, at the aiError block's old position,
          so every equal-z-index paint order in this list is unchanged. */}
      <AccountDialogLayer />
      {exportHubOpen && <ExportHubDialog open initialTab={exportHubInitialTab} onClose={() => setExportHubOpen(false)} />}
      {aiTtsOpen && <AiTtsDialog open onClose={closeAiTtsDialog} />}
      {shareOpen && <ShareDialog open onClose={() => setShareOpen(false)} />}
      {subscriptionGateOpen && (
        <div className={styles.aiErrorOverlay} onClick={() => setSubscriptionGateOpen(false)}>
          <div className={`${styles.aiErrorDialog} ${styles.subscriptionGateDialog}`} onClick={e => e.stopPropagation()}>
            <div className={`${styles.aiErrorTitle} ${styles.subscriptionGateTitle}`}>{t('subscriptionRequiredTitle')}</div>
            <div className={styles.aiErrorMessage}>
              {t('subscriptionRequiredMessage')}
            </div>
            <div className={styles.subscriptionGateActions}>
              <button className={styles.subscriptionGateCancel} onClick={() => setSubscriptionGateOpen(false)}>{t('subscriptionGateCancel')}</button>
              <button className={styles.aiErrorDismiss} onClick={() => {
                setSubscriptionGateOpen(false);
                import('@/bridge/embeddedBridge.ts').then(({ enqueueSendToNative }) => {
                  enqueueSendToNative('showPurchasePanel', {});
                });
              }}>{t('subscriptionGateContinue')}</button>
            </div>
          </div>
        </div>
      )}
      {earnCreditsDialogOpen && <EarnCreditsDialog open onClose={() => setEarnCreditsDialogOpen(false)} />}
      {/* IapSuccessDialog used to render HERE. Moved into AccountDialogLayer (mounted above at the
          "Dialogs" comment) on 2026-08-22 so the Board and Screenplay routes get it too — buying
          credits from the board's in-place Enhance dialog set iapSuccessInfo with no renderer on
          screen, and the confirmation ambushed the user on their next visit to the Timeline. Do NOT
          re-add it here: this component mounts the layer, so a copy here is a DOUBLE render. */}
      <RewardNotifier />
      {historyPanelOpen && <GenerationHistoryPanel />}
    </div>
  );
}
